import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';
import TeamBulkUpload from '../../components/admin/TeamBulkUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    bio: '',
    franchise_id: '',
    photo: '',
    email: '',
    phone: '',
    instagram: '',
    linkedin: '',
    specialties: [],
    order: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [membersRes, franchisesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/team`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/franchises`, { withCredentials: true })
      ]);
      setMembers(membersRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await axios.put(
          `${API_URL}/admin/team/${editingMember.member_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Membro atualizado!');
      } else {
        await axios.post(`${API_URL}/admin/team`, formData, {
          withCredentials: true
        });
        toast.success('Membro criado!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar membro');
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      bio: member.bio,
      franchise_id: member.franchise_id,
      photo: member.photo || '',
      email: member.email || '',
      phone: member.phone || '',
      instagram: member.instagram || '',
      linkedin: member.linkedin || '',
      specialties: member.specialties || [],
      order: member.order || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Tem certeza que deseja excluir este membro?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/team/${memberId}`, {
        withCredentials: true
      });
      toast.success('Membro excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir membro');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      bio: '',
      franchise_id: '',
      photo: '',
      email: '',
      phone: '',
      instagram: '',
      linkedin: '',
      specialties: [],
      order: 0
    });
    setEditingMember(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getFranchiseName = (franchiseId) => {
    const franchise = franchises.find(f => f.franchise_id === franchiseId);
    return franchise ? `${franchise.city} - ${franchise.state}` : '';
  };

  // Group by franchise
  const membersByFranchise = members.reduce((acc, member) => {
    const key = member.franchise_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(member);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold mb-2">Equipe IE</h2>
          <p className="text-neutral-600">Gerencie os membros da equipe por franquia</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="flex items-center gap-2"
        >
          <iconify-icon icon="solar:user-plus-linear"></iconify-icon>
          Novo Membro
        </Button>
      </div>

      {/* Bulk photo upload */}
      <TeamBulkUpload onComplete={loadData} />

      {/* Members by Franchise */}
      <div className="space-y-6">
        {Object.entries(membersByFranchise).map(([franchiseId, franchiseMembers]) => (
          <div key={franchiseId} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            <div className="bg-neutral-50 px-6 py-4 border-b border-neutral-200">
              <h3 className="font-bold text-lg">
                {getFranchiseName(franchiseId)} ({franchiseMembers.length})
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {franchiseMembers.map((member) => (
                <div key={member.member_id} className="border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-start gap-4 mb-4">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-neutral-200 flex items-center justify-center">
                        <iconify-icon icon="solar:user-linear" className="text-3xl text-zinc-700"></iconify-icon>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate">{member.name}</h4>
                      <p className="text-sm text-neutral-500">{member.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mb-4 line-clamp-3">{member.bio}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="flex-1 px-3 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(member.member_id)}
                      className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                    >
                      <iconify-icon icon="solar:trash-bin-linear" className="text-xl"></iconify-icon>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center py-12 text-neutral-500 bg-white rounded-xl border border-neutral-200">
          <iconify-icon icon="solar:users-group-rounded-linear" className="text-6xl mb-4"></iconify-icon>
          <p>Nenhum membro cadastrado ainda</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Editar Membro' : 'Novo Membro'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Cargo *</Label>
                <Input
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="franchise_id">Franquia *</Label>
              <Select
                value={formData.franchise_id}
                onValueChange={(value) => setFormData({ ...formData, franchise_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a franquia" />
                </SelectTrigger>
                <SelectContent>
                  {franchises.map((franchise) => (
                    <SelectItem key={franchise.franchise_id} value={franchise.franchise_id}>
                      {franchise.city} - {franchise.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bio">Biografia *</Label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className="w-full border border-neutral-300 rounded-md p-2 min-h-[100px]"
                required
              />
            </div>
            <div>
              <Label>Foto</Label>
              <FileUploader
                value={formData.photo}
                onChange={(url) => setFormData({ ...formData, photo: url })}
                accept="image"
                folder="team"
                testId="team-photo-uploader"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="@usuario"
                />
              </div>
              <div>
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  placeholder="URL do perfil"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="order">Ordem de Exibição</Label>
              <Input
                id="order"
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">{editingMember ? 'Atualizar' : 'Criar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManager;
